import { type FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { can } from '../lib/permissions';
import {
  PRIORITIES,
  TASK_STATUSES,
  type ChecklistItem,
  type Priority,
  type Task,
  type TaskStatus,
} from '../lib/types';
import { useStore } from '../store/useStore';

const STATUS_LABEL: Record<TaskStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

/**
 * Grows with its content, so a long description reads as a page, not a scroll box.
 *
 * `shown` is not optional: a `<dialog>` that has not been opened yet is
 * `display: none`, every box inside it measures zero, and a height computed then
 * would stick — the field has no reason to be re-measured afterwards.
 */
function useAutoHeight(value: string, shown: boolean) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || !shown) return;
    node.style.height = 'auto';
    node.style.height = `${node.scrollHeight}px`;
  }, [value, shown]);
  return ref;
}

/**
 * The task opened up: a small document. The board shows its title; everything
 * else about it lives here — the body text, the steps inside it, and who it
 * belongs to.
 */
export function TaskDialog({ task, onClose }: { task: Task; onClose: () => void }) {
  const fields = useStore((s) => s.fields);
  const members = useStore((s) => s.members);
  const access = useStore((s) => s.access);
  const user = useStore((s) => s.user);
  const updateTask = useStore((s) => s.updateTask);
  const deleteTask = useStore((s) => s.deleteTask);

  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [shown, setShown] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleRef = useAutoHeight(title, shown);
  const bodyRef = useAutoHeight(description, shown);

  useEffect(() => {
    dialogRef.current?.showModal();
    setShown(true);
  }, []);

  const field = task.fieldId ? fields.find((f) => f.id === task.fieldId) : undefined;
  const assignee = task.assigneeId ? members.find((m) => m.id === task.assigneeId) : undefined;
  const completedBy = task.completedBy ? members.find((m) => m.id === task.completedBy) : undefined;

  /** Title and description are committed on blur so typing is not a write per keystroke. */
  function commitTitle() {
    const trimmed = title.trim();
    if (trimmed && trimmed !== task.title) void updateTask(task.id, { title: trimmed });
    else if (!trimmed) setTitle(task.title);
  }

  function commitDescription() {
    if (description !== task.description) void updateTask(task.id, { description });
  }

  // What this dialog is: a document to read, one to work through, or one to
  // rewrite. Which of the three is a per-member setting, so every control asks
  // for the permission it actually needs rather than for a role.
  const mayEdit = can(access, 'task.edit');
  const mayClaim = can(access, 'task.claim');
  const mayAssign = can(access, 'task.assign');
  const mayDelete = can(access, 'task.delete');

  // Taking work and handing it back is claiming; naming somebody else is the
  // separate grant, and the server enforces the same split.
  const assignableMembers = mayAssign ? members : members.filter((m) => m.id === user?.id);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) dialogRef.current?.close();
      }}
      className="m-auto w-[min(42rem,calc(100vw-2rem))] rounded-xl border border-line bg-surface p-0 text-ink backdrop:bg-black/60 backdrop:backdrop-blur-sm"
    >
      <div className="flex max-h-[85dvh] flex-col overflow-y-auto">
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
          <span className="mt-1.5 flex min-w-0 items-center gap-2">
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: field?.color ?? 'var(--color-ink-faint)' }}
            />
            <span className="truncate text-[11px] tracking-wide text-ink-faint uppercase">
              {field?.name ?? 'Unsorted'}
            </span>
          </span>
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            className="btn btn-ghost ml-auto !border-transparent !px-2"
            aria-label="Close"
          >
            <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
              <path d="M4.28 3.22a.75.75 0 1 0-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 1 0 1.06 1.06L8 9.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L9.06 8l3.72-3.72a.75.75 0 0 0-1.06-1.06L8 6.94 4.28 3.22Z" />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-6 px-5 py-5">
          <textarea
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            readOnly={!mayEdit}
            rows={1}
            maxLength={300}
            aria-label="Task title"
            className="field-input resize-none overflow-hidden !border-transparent !bg-transparent !px-0 text-lg leading-snug font-semibold focus:!border-transparent"
          />

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Select
              label="Status"
              value={task.status}
              disabled={!mayClaim}
              onChange={(value) => void updateTask(task.id, { status: value as TaskStatus })}
              options={TASK_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
            />
            <Select
              label="Priority"
              value={task.priority}
              disabled={!mayEdit}
              onChange={(value) => void updateTask(task.id, { priority: value as Priority })}
              options={PRIORITIES.map((p) => ({
                value: p,
                label: p[0]!.toUpperCase() + p.slice(1),
              }))}
            />
            {mayEdit ? (
              <Select
                label="Dev field"
                value={task.fieldId ?? ''}
                onChange={(value) => void updateTask(task.id, { fieldId: value || null })}
                options={[
                  { value: '', label: 'Unsorted' },
                  ...fields.map((f) => ({ value: f.id, label: f.name })),
                ]}
              />
            ) : (
              <Readonly label="Dev field" value={field?.name ?? 'Unsorted'} />
            )}
            <Select
              label="Assignee"
              value={task.assigneeId ?? ''}
              disabled={!mayClaim && !mayAssign}
              onChange={(value) => void updateTask(task.id, { assigneeId: value || null })}
              options={[
                { value: '', label: 'Unassigned' },
                ...assignableMembers.map((m) => ({
                  value: m.id,
                  label: m.id === user?.id ? `${m.displayName} (me)` : m.displayName,
                })),
                // An assignee the viewer may not re-pick still has to be shown, or
                // the select would silently misreport who owns the task.
                ...(assignee && !assignableMembers.some((m) => m.id === assignee.id)
                  ? [{ value: assignee.id, label: assignee.displayName }]
                  : []),
              ]}
            />
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-soft">Description</span>
            <textarea
              ref={bodyRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={commitDescription}
              readOnly={!mayEdit}
              rows={4}
              maxLength={10_000}
              placeholder="Write it out — context, decisions, links, how to tell it is finished."
              className="field-input min-h-28 resize-none overflow-hidden text-sm leading-relaxed"
            />
          </label>

          <Checklist
            items={task.checklist ?? []}
            mayTick={mayClaim}
            mayEdit={mayEdit}
            onChange={(checklist) => void updateTask(task.id, { checklist })}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4">
          <div className="flex min-w-0 flex-col gap-0.5 text-[11px] text-ink-faint">
            {task.completedAt && (
              <span>
                Completed {completedBy ? `by ${completedBy.displayName} ` : ''}
                {formatMoment(task.completedAt)}
              </span>
            )}
            <span>Created {formatMoment(task.createdAt)}</span>
          </div>

          {confirmingDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-soft">Delete this task?</span>
              <button
                type="button"
                onClick={() => {
                  void deleteTask(task.id);
                  dialogRef.current?.close();
                }}
                className="btn btn-danger !py-1 !text-xs"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="btn btn-ghost !py-1 !text-xs"
              >
                Keep
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {mayDelete && (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  className="btn btn-danger !py-1 !text-xs"
                >
                  Delete task
                </button>
              )}
              <button
                type="button"
                onClick={() => dialogRef.current?.close()}
                className="btn btn-ghost !py-1 !text-xs"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}

/**
 * The task's inner tasks. Structural changes (add, tick, remove) are written as
 * they happen; a rename waits for the field to lose focus, like the body text.
 */
function Checklist({
  items,
  mayTick,
  mayEdit,
  onChange,
}: {
  items: ChecklistItem[];
  /** Ticking a step off is working through the task. */
  mayTick: boolean;
  /** Adding, renaming or removing one is rewriting it. */
  mayEdit: boolean;
  onChange: (items: ChecklistItem[]) => void;
}) {
  const [adding, setAdding] = useState('');
  const [draft, setDraft] = useState<{ id: string; title: string } | null>(null);

  const completed = items.filter((item) => item.done).length;

  function add(event: FormEvent) {
    event.preventDefault();
    const title = adding.trim();
    if (!title) return;
    onChange([...items, { id: crypto.randomUUID(), title, done: false }]);
    setAdding('');
  }

  function rename(id: string, title: string) {
    const trimmed = title.trim();
    setDraft(null);
    if (!trimmed) return;
    const current = items.find((item) => item.id === id);
    if (!current || current.title === trimmed) return;
    onChange(items.map((item) => (item.id === id ? { ...item, title: trimmed } : item)));
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-ink-soft">Steps</span>
        {items.length > 0 && (
          <>
            <span className="text-xs text-ink-faint tabular-nums">
              {completed} of {items.length}
            </span>
            {/* Meter: fill and track are two steps of the one hue, so the state
                reads across the whole bar. */}
            <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-accent-soft">
              <span
                className="block h-full rounded-full bg-accent transition-[width] duration-200"
                style={{ width: `${(completed / items.length) * 100}%` }}
              />
            </span>
          </>
        )}
      </div>

      {items.length > 0 && (
        <ul className="flex flex-col divide-y divide-line overflow-hidden rounded-lg border border-line">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-2 py-1.5">
              <button
                type="button"
                onClick={() =>
                  onChange(
                    items.map((row) => (row.id === item.id ? { ...row, done: !row.done } : row)),
                  )
                }
                aria-pressed={item.done}
                disabled={!mayTick}
                aria-label={item.done ? `Undo ${item.title}` : `Finish ${item.title}`}
                className={`grid size-4 shrink-0 place-items-center rounded border transition-colors enabled:cursor-pointer ${
                  item.done
                    ? 'border-done bg-done text-canvas'
                    : 'border-line-strong text-transparent enabled:hover:border-done'
                }`}
              >
                <svg viewBox="0 0 16 16" className="size-3" fill="currentColor" aria-hidden="true">
                  <path d="M13.5 4.2a.9.9 0 0 1 0 1.28l-6.1 6.1a.9.9 0 0 1-1.28 0L2.9 8.36a.9.9 0 0 1 1.28-1.27l2.58 2.58 5.46-5.47a.9.9 0 0 1 1.28 0Z" />
                </svg>
              </button>

              <input
                value={draft?.id === item.id ? draft.title : item.title}
                onFocus={() => setDraft({ id: item.id, title: item.title })}
                onChange={(event) => setDraft({ id: item.id, title: event.target.value })}
                onBlur={(event) => rename(item.id, event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur();
                }}
                readOnly={!mayEdit}
                maxLength={300}
                aria-label="Step"
                className={`field-input !border-transparent !bg-transparent !px-1 !py-0.5 text-sm focus:!border-line ${
                  item.done ? 'text-ink-faint line-through' : ''
                }`}
              />

              <button
                type="button"
                hidden={!mayEdit}
                onClick={() => onChange(items.filter((row) => row.id !== item.id))}
                aria-label={`Remove ${item.title}`}
                className="shrink-0 cursor-pointer rounded p-1 text-ink-faint transition-colors hover:bg-hover hover:text-danger"
              >
                <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
                  <path d="M4.28 3.22a.75.75 0 1 0-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 1 0 1.06 1.06L8 9.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L9.06 8l3.72-3.72a.75.75 0 0 0-1.06-1.06L8 6.94 4.28 3.22Z" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={add} hidden={!mayEdit} className="flex gap-2">
        <input
          value={adding}
          onChange={(event) => setAdding(event.target.value)}
          placeholder="Add a step…"
          maxLength={300}
          className="field-input text-sm"
        />
        {adding.trim() && (
          <button type="submit" className="btn btn-ghost !py-1.5 !text-xs">
            Add
          </button>
        )}
      </form>
    </section>
  );
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Readonly({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-soft">{label}</span>
      <span className="field-input truncate text-sm text-ink-soft">{value}</span>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  /** Shown but not changeable, for a field this member may read and not set. */
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-ink-soft">{label}</span>
      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="field-input text-sm disabled:text-ink-soft"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
